import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export type ReplyStatus = 'new' | 'drafted' | 'sent' | 'ignored';

export interface IgComment {
  comment_id: string;
  media_id: string;
  media_permalink: string | null;
  comment_text: string | null;
  comment_username: string | null;
  comment_timestamp: string | null;
  like_count: number | null;
  reply_status: ReplyStatus;
  reply_text: string | null;
  reply_sent_at: string | null;
  ai_reply_ms: number | null;
  updated_at: string;
}

export function useIgComments(filter: 'unanswered' | 'all' = 'unanswered') {
  return useQuery<IgComment[]>({
    queryKey: ['ig-comments', filter],
    queryFn: async () => {
      let q = marketing
        .from('ig_comments')
        .select('*')
        .order('comment_timestamp', { ascending: false })
        .limit(100);
      if (filter === 'unanswered') q = q.in('reply_status', ['new', 'drafted']);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as IgComment[];
    },
    staleTime: 30_000,
  });
}

export function useUpdateComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ comment_id, patch }: { comment_id: string; patch: Partial<IgComment> }) => {
      const { error } = await marketing.from('ig_comments').update(patch).eq('comment_id', comment_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ig-comments'] }),
  });
}
